import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SearchBar = ({ onSearch, onFilterChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: 'recent',
    timeRange: 'all',
    hasImages: false,
    verified: false
  });

  const handleSearch = (query) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'helpful', label: 'Most Helpful' },
    { value: 'trending', label: 'Trending' }
  ];

  const timeRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'year', label: 'This Year' }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6">
      {/* Search Input */}
      <div className="relative mb-4">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon name="Search" size={20} className="text-accent-400" />
        </div>
        <Input
          type="search"
          placeholder="Search posts, users, or hashtags..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10 pr-12"
        />
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`absolute inset-y-0 right-0 pr-3 flex items-center transition-colors ${
            showFilters ? 'text-primary' : 'text-accent-400 hover:text-accent-600'
          }`}
        >
          <Icon name="SlidersHorizontal" size={20} />
        </button>
      </div>

      {/* Quick Search Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['#giftideas', '#birthday', '#anniversary', '#diy', '#budget', '#luxury'].map((tag) => (
          <button
            key={tag}
            onClick={() => handleSearch(tag)}
            className="px-3 py-1 bg-surface text-accent-600 rounded-full text-sm hover:bg-accent-100 hover:text-accent-800 transition-colors"
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="border-t border-accent-100 pt-4 space-y-4">
          <h4 className="font-semibold text-accent-800">Filters</h4>
          
          {/* Sort By */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Sort By
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="w-full p-2 border border-accent-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Time Range
            </label>
            <select
              value={filters.timeRange}
              onChange={(e) => handleFilterChange('timeRange', e.target.value)}
              className="w-full p-2 border border-accent-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            >
              {timeRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Filters */}
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="hasImages"
                checked={filters.hasImages}
                onChange={(e) => handleFilterChange('hasImages', e.target.checked)}
                className="w-4 h-4 text-primary border-accent-300 rounded focus:ring-primary"
              />
              <label htmlFor="hasImages" className="text-sm text-accent-700">
                Posts with images only
              </label>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="verified"
                checked={filters.verified}
                onChange={(e) => handleFilterChange('verified', e.target.checked)}
                className="w-4 h-4 text-primary border-accent-300 rounded focus:ring-primary"
              />
              <label htmlFor="verified" className="text-sm text-accent-700">
                Verified users only
              </label>
            </div>
          </div>

          {/* Clear Filters */}
          <button
            onClick={() => {
              const defaultFilters = {
                sortBy: 'recent',
                timeRange: 'all',
                hasImages: false,
                verified: false
              };
              setFilters(defaultFilters);
              onFilterChange(defaultFilters);
            }}
            className="text-sm text-primary hover:text-primary-600 transition-colors"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;